import { useEffect } from 'react';
import type { ErrorComponentProps } from '@tanstack/react-router';
import { Link, useRouter } from '@tanstack/react-router';
import { Badge, Card } from './ui';

/**
 * What renders when a screen throws instead of rendering.
 *
 * There was nothing here before this: no `componentDidCatch`, no
 * `getDerivedStateFromError`, no router `errorComponent` anywhere in the repo.
 * A render that threw took the whole console to a blank white page — which is
 * not hypothetical, it is how this week went. `searchableFields` filtered on
 * `field is string` and let one `undefined` field through; the owner typeahead
 * called `.toLowerCase()` on it on the first keystroke, and every operator who
 * had that screen open lost the entire app, not just the one field that was
 * wrong.
 *
 * `ApiErrorNotice` is the closest thing to a precedent — it explains a failure
 * without being able to fail itself — and this borrows its voice. It is not
 * reused, because it is answering a different question: `ApiErrorNotice` says
 * the API refused or could not be reached, which is true of the world outside
 * the console. This says the console's own code is broken, which is true of
 * nothing else — an operator who is told "an upstream system failed" here
 * would go and look at Proxmox for a bug that lives entirely in this bundle.
 *
 * The one rule that matters more than the copy: this component is the last
 * thing standing once everything else has failed, so it cannot be the next
 * thing to fail. No query, no mutation, no hook that assumes a context is
 * there to read. `useRouter({ warn: false })` reads router context without
 * throwing when there is none — the same guard `RouteLink` in `ui.tsx` uses
 * to let `CopyableId` survive a render with no router underneath it — because
 * a boundary that requires the very thing that just broke is not a boundary.
 */
export function RouteFailure({ error }: ErrorComponentProps) {
  // The one place this is guaranteed to still be logged. React's own dev
  // warning for a caught error does not run in production, and swallowing the
  // error entirely — showing only friendly copy — would turn every crash
  // permanently undebuggable.
  useEffect(() => {
    console.error(error);
  }, [error]);

  return (
    <Card title="This screen has a bug">
      <div role="alert">
        <p>
          <Badge tone="bad">bug</Badge> This screen failed while rendering, because of a defect in
          platformctl&rsquo;s own code. It is not a refusal and it is not an outage in anything
          this console depends on.
        </p>
        <p>{messageFor(error)}</p>
        <p>
          Reloading clears a one-off render failure. If the same screen keeps failing, report it
          with the message above.
        </p>
        <p>
          <button type="button" onClick={() => window.location.reload()}>
            Reload
          </button>
          {' · '}
          <HomeLink />
        </p>
      </div>
    </Card>
  );
}

/**
 * What the thrown value actually says, without assuming it is an `Error`.
 *
 * TanStack Router types this as `Error`, but that is a claim about the common
 * case, not a guarantee: `throw 'not a name'` and `throw { oops: true }` are
 * both legal JavaScript, and a component whose entire purpose is to survive
 * whatever broke must not assume the thing that broke followed convention on
 * its way down.
 */
function messageFor(error: unknown): string {
  if (error instanceof Error) {
    const message = error.message;
    if (typeof message === 'string' && message.trim().length > 0) return message;
  }
  return 'No further detail was thrown with this error.';
}

/**
 * A link to the one screen that does not depend on whatever just broke.
 *
 * Same shape as `RouteLink` in `ui.tsx`, for the same reason: `<Link>` reads
 * router context unguarded and throws when there is none, which would make the
 * fallback path of an error boundary the one path that can itself error. This
 * component is always rendered inside the real router in production — it is
 * wired in as `router.ts`'s `defaultErrorComponent` — so the guard is not
 * expected to bite there; it exists so a unit test can render `RouteFailure`
 * on its own, with nothing standing in for a router, and still get a page back
 * rather than a thrown exception.
 */
function HomeLink() {
  const router: unknown = useRouter({ warn: false });

  if (!router) {
    return <a href="/">the home screen</a>;
  }

  return <Link to="/">the home screen</Link>;
}
